export type CapabilityMilestone = {
  label: string
  // Where the milestone sits on Plan A's DISPLAYED uplift curve (the
  // "capability shaped" sheet row): the shaped row's value at the date the
  // underlying "(model)" row crosses `modelValue`. Naming/positioning against
  // the displayed curve uses this, so milestones land on the correct dates.
  value: number
  // The canonical aifm cutoff that DEFINES the milestone, on the underlying
  // model-uplift scale (TED-AI = 419x model uplift). The displayed numeric
  // labels come from this scale, not from `value`.
  modelValue: number
  // Rounded display form of `modelValue` for labels ("20x" for 22). Hand-set
  // because the desired rounding isn't strict 1-significant-figure
  // (9,463 displays as "10,000x", not "9,000x").
  displayValue: string
  subtitle?: string
  planALabel?: string
  color?: string
  tooltip?: React.ReactNode
}

export type RiskLevel = {
  riskLabel: string
  value: number
}

// PURE capability milestones — these drive the extrapolated graph's marker
// labels / current-point noun AND the labor-mix copies-speed noun, and
// nothing else. Each milestone is DEFINED by its aifm model-uplift cutoff
// (`modelValue`: Reliable 1.2x, AC 3.1x, SAR 22x, TED-AI 419x, WSI 9463x) —
// the numeric labels always show that scale. The `value` is where that
// crossing lands on Plan A's displayed "capability shaped" uplift row
// ([wip]plan-a-side-panel row 19), so naming against the displayed curve
// fires on the underlying model row's dates (Reliable @2026.8, AC @2030.5,
// SAR @2032.25, TED-AI @2035, WSI @2041.2). Unreliable Agent is everything
// below Reliable Agent.
export const capabilityMilestones: CapabilityMilestone[] = [
  {
    label: "Unreliable Agent",
    value: 1,
    modelValue: 1,
    displayValue: "1x",
  },
  {
    label: "Reliable Agent",
    value: 1.2,
    modelValue: 1.2,
    displayValue: "1.2x",
  },
  {
    label: "Automated coder",
    value: 3.09,
    modelValue: 3.1,
    displayValue: "3x",
  },
  {
    label: "Superhuman AI Researcher",
    planALabel: "Superhuman STEM Researcher",
    value: 10.2,
    modelValue: 22,
    displayValue: "20x",
  },
  {
    label: "Top-Expert-Dominating AI",
    tooltip: "Top-Expert-Dominating AI (TED AI)",
    value: 40.4,
    modelValue: 419,
    displayValue: "400x",
  },
  {
    label: "Superintelligent",
    value: 276,
    modelValue: 9463,
    displayValue: "1,000x",
  },
  {
    label: "Wildly Superintelligent",
    value: 1104,
    modelValue: 9463,
    displayValue: "10,000x",
  },
  {
    label: "Galaxy Brain",
    value: 7501608,
    modelValue: 9463,
    displayValue: "10,000x",
  },
]

// Risk ladder — SPECIFIED SEPARATELY from the capability milestones (the
// takeover-risk hover line and any risk-band logic read this, never the
// milestone list). It has its own extra step (formerly 100x, now 20x) with
// no capability milestone attached, which is why the two must not share one
// array. Values remapped to the capability-shaped uplift row the same way as
// the milestones (same crossing date on the old "(model)" row, new row's
// value there — the extra step's date was 2033.4).
export const riskLevels: RiskLevel[] = [
  { riskLabel: "Negligible Risk", value: 1 },
  { riskLabel: "Very Low Risk", value: 1.2 },
  { riskLabel: "Some Risk", value: 3.09 },
  { riskLabel: "Moderate Risk", value: 10.2 },
  { riskLabel: "High Risk", value: 21.7 },
  { riskLabel: "Very High Risk", value: 40.4 },
  { riskLabel: "Extreme Risk", value: 395 },
]

// Highest milestone reached by a capability (speedup) value — the labeling
// rule the extrapolated graph uses for its current-point label and hover
// tooltips. Exported so other blocks (e.g. the labor-mix legend's AI-agents
// noun) render the exact same wording as the chart.
export const getCapabilityThresholdLabelForValue = (speedupValue: number): string => {
  if (capabilityMilestones.length === 0) return 'Unknown'
  if (speedupValue < capabilityMilestones[0].value) {
    return `Below ${capabilityMilestones[0].label}`
  }
  let latestReachedMilestoneLabel = capabilityMilestones[0].label
  for (const milestone of capabilityMilestones) {
    if (speedupValue < milestone.value) break
    latestReachedMilestoneLabel = milestone.label
  }
  return latestReachedMilestoneLabel
}

// ─── Plan-aware capability staging ───────────────────────────────────────────
// One ladder per display scale, shared by every surface that names a plan's
// capability stage (the sheet tabs' "Capability stage" rows, the graph's
// hover noun + milestone markers, and the labor-mix copies/speed noun), so
// they all switch stages at the same moments:
// - Plan A plots the "capability shaped" row and (by authorial choice)
//   recognizes only AC → TED → ASI (no SAR stop on the shaped curve), plus
//   the two late-game shaped-scale tiers (WSI → GB) the epilogue climbs
//   through — the shaped data's 2050 endpoint sits just past Galaxy Brain's
//   cutoff, so the story's final beat lands on Galaxy Brain.
// - The branch plans (B/C/D/S) plot model-scale uplift and stage on the
//   canonical aifm cutoffs with the full AC → SAR → TED → ASI ladder; the
//   model row caps at ASI's 9463x, so the late-game tiers have no model-scale
//   cutoffs and only exist on Plan A's ladder.
export type CapabilityStageShort = 'AC' | 'SAR' | 'TED' | 'ASI' | 'WSI' | 'GB'
export type CapabilityStageScale = 'planA' | 'model'

export const CAPABILITY_STAGE_LADDERS: Record<
  CapabilityStageScale,
  Array<{ short: CapabilityStageShort; atOrAbove: number; label: string; displayValue: string }>
> = {
  planA: [
    { short: 'AC', atOrAbove: 3.09, label: 'Automated coder', displayValue: '3x' },
    { short: 'TED', atOrAbove: 40.4, label: 'Top-Expert-Dominating AI', displayValue: '400x' },
    { short: 'ASI', atOrAbove: 276, label: 'Superintelligent', displayValue: '1,000x' },
    { short: 'WSI', atOrAbove: 1104, label: 'Wildly Superintelligent', displayValue: '10,000x' },
    { short: 'GB', atOrAbove: 7500000, label: 'Galaxy Brain', displayValue: '10,000x' },
  ],
  model: [
    { short: 'AC', atOrAbove: 3.1, label: 'Automated coder', displayValue: '3x' },
    { short: 'SAR', atOrAbove: 22, label: 'Superhuman AI Researcher', displayValue: '20x' },
    { short: 'TED', atOrAbove: 419, label: 'Top-Expert-Dominating AI', displayValue: '400x' },
    { short: 'ASI', atOrAbove: 9463, label: 'Superintelligent', displayValue: '1,000x' },
  ],
}

export const capabilityStageScaleForPlan = (plan?: string | null): CapabilityStageScale =>
  plan === 'A' ? 'planA' : 'model'

// Shorthand stage ('' below AC) — the per-plan sheet tabs' "Capability
// stage" rows use exactly this.
export const capabilityStageShort = (
  value: number,
  scale: CapabilityStageScale,
): CapabilityStageShort | '' => {
  let reached: CapabilityStageShort | '' = ''
  for (const stage of CAPABILITY_STAGE_LADDERS[scale]) {
    if (value < stage.atOrAbove) break
    reached = stage.short
  }
  return reached
}

// Highest stage reached on a plan's plotted value, with the pre-AC
// milestones (Unreliable/Reliable Agent — identical on both scales) kept
// from the generic list. The plan-aware labeling rule for the graph's
// current-point label / hover noun and the copies-speed noun.
export const getCapabilityStageLabelForPlanValue = (
  speedupValue: number,
  plan?: string | null,
): string => {
  const ladder = CAPABILITY_STAGE_LADDERS[capabilityStageScaleForPlan(plan)]
  if (speedupValue < ladder[0].atOrAbove) {
    if (speedupValue < capabilityMilestones[0].value) {
      return `Below ${capabilityMilestones[0].label}`
    }
    // Only the two pre-AC entries (Unreliable/Reliable Agent) apply here.
    return speedupValue >= capabilityMilestones[1].value
      ? capabilityMilestones[1].label
      : capabilityMilestones[0].label
  }
  let reached = ladder[0]
  for (const stage of ladder) {
    if (speedupValue < stage.atOrAbove) break
    reached = stage
  }
  return reached.label
}

// Milestone-marker specs for the graph overlay, on the plan's plotted scale:
// the two pre-AC milestones plus the plan's ladder (Plan A also keeps its
// late-game Wildly Superintelligent marker on the shaped scale).
export const getCapabilityMarkerSpecs = (
  plan?: string | null,
): Array<{ label: string; value: number; displayValue: string }> => {
  const scale = capabilityStageScaleForPlan(plan)
  const pre = capabilityMilestones
    .slice(0, 2)
    .map((m) => ({ label: m.label, value: m.value, displayValue: m.displayValue }))
  const ladder = CAPABILITY_STAGE_LADDERS[scale].map((s) => ({
    label: s.label,
    value: s.atOrAbove,
    displayValue: s.displayValue,
  }))
  const lateGame = scale === 'planA'
    ? [{ label: 'Wildly Superintelligent', value: 1104, displayValue: '10,000x' }]
    : []
  return [...pre, ...ladder, ...lateGame]
}
